import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { QRCodeSVG } from 'qrcode.react';
import { FiCalendar, FiMapPin, FiUser, FiCheck, FiShare2, FiExternalLink } from 'react-icons/fi';

const THEME_COLORS = {
  elegant: { bg: '#2D1F2B', accent: '#E8A0BF', text: '#FFF5F8' },
  traditional: { bg: '#8B2252', accent: '#F2C4D6', text: '#FFF8F0' },
  modern: { bg: '#0F0F0F', accent: '#FFFFFF', text: '#FFFFFF' },
  floral: { bg: '#2D1B2E', accent: '#F0B6C5', text: '#FFF8F0' },
  minimal: { bg: '#FFF5F8', accent: '#C97191', text: '#2D1F2B' },
  gold: { bg: '#2D1F2B', accent: '#E8A0BF', text: '#FFF5F8' },
};

function isLightColor(hex) {
  if (!hex) return false;
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
}

export default function TicketView() {
  const { ticketCode } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [error, setError] = useState(false);

  // Parse query params: ?share=inviteShareLink&all_tickets=GGR-XXX,GGR-YYY
  const params = new URLSearchParams(window.location.search);
  const inviteShareLink = params.get('share') || '';
  const allTickets = (params.get('all_tickets') || '').split(',').filter(Boolean);

  useEffect(() => {
    api.get(`/ticket/${ticketCode}`)
      .then(r => setTicket(r.data))
      .catch(() => setError(true));
  }, [ticketCode]);

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1A1A2E', padding: '20px' }}>
        <div style={{ textAlign: 'center', color: '#FFF5F8' }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>🎫</div>
          <h1 style={{ fontSize: '28px', marginBottom: '12px', fontFamily: "'Playfair Display', serif" }}>Ticket Not Found</h1>
          <p style={{ opacity: 0.6, fontFamily: 'Inter, sans-serif' }}>This ticket code may be invalid or has been removed.</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1A1A2E' }}><div style={{ width: '40px', height: '40px', border: '3px solid #E8A0BF33', borderTop: '3px solid #E8A0BF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;
  }

  const customColors = ticket.colors && typeof ticket.colors === 'object' && ticket.colors.bg ? ticket.colors : null;
  const colors = ticket.theme === 'custom' && customColors ? customColors : (THEME_COLORS[ticket.theme] || THEME_COLORS.elegant);
  const isLight = ticket.theme === 'minimal' || (customColors && isLightColor(customColors.bg));
  const bannerImage = ticket.custom_image || ticket.cover_image;
  const eventDate = ticket.event_date ? new Date(ticket.event_date) : null;
  const ticketUrl = `${window.location.origin}/ticket/${ticket.ticket_code}`;

  const shareWhatsApp = () => {
    const text = `My ticket for ${ticket.event_title}!\n\nTicket: ${ticket.ticket_code}\n${ticketUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, sans-serif' }}>
      {/* Background image overlay */}
      {bannerImage && <div style={{ position: 'fixed', inset: 0, backgroundImage: `url(${bannerImage})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: isLight ? 0.06 : 0.1, filter: 'blur(3px)', pointerEvents: 'none' }} />}

      <div style={{ position: 'relative', width: '100%', maxWidth: '420px' }}>
        {/* Ticket card */}
        <div style={{ background: isLight ? '#fff' : 'rgba(255,255,255,0.06)', borderRadius: '24px', overflow: 'hidden', border: `1px solid ${colors.accent}22`, boxShadow: `0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px ${colors.accent}10` }}>

          {/* Banner section */}
          <div style={{ position: 'relative', height: '200px', overflow: 'hidden' }}>
            {bannerImage ? (
              <img src={bannerImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, ${colors.accent}40, ${colors.accent}15)` }} />
            )}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent 30%, rgba(0,0,0,0.7))' }} />
            <div style={{ position: 'absolute', bottom: '20px', left: '24px', right: '24px' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '3px', color: colors.accent, fontWeight: 700, marginBottom: '6px' }}>Digital Ticket</div>
              <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#fff', fontFamily: "'Playfair Display', serif", lineHeight: 1.2 }}>{ticket.event_title}</h1>
            </div>
          </div>

          {/* Ticket tear line */}
          <div style={{ position: 'relative', height: '24px', display: 'flex', alignItems: 'center' }}>
            <div style={{ position: 'absolute', left: '-12px', width: '24px', height: '24px', borderRadius: '50%', background: colors.bg }} />
            <div style={{ flex: 1, borderBottom: `2px dashed ${colors.accent}25`, margin: '0 20px' }} />
            <div style={{ position: 'absolute', right: '-12px', width: '24px', height: '24px', borderRadius: '50%', background: colors.bg }} />
          </div>

          {/* Ticket details */}
          <div style={{ padding: '20px 28px 28px' }}>
            {/* Guest name */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', color: colors.accent, fontWeight: 600, marginBottom: '6px' }}>Admit One</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: colors.text, fontFamily: "'Playfair Display', serif" }}>{ticket.guest_name}</div>
            </div>

            {/* Event info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              {eventDate && (
                <div style={{ padding: '14px', background: `${colors.accent}08`, borderRadius: '12px', border: `1px solid ${colors.accent}12` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: colors.accent, fontWeight: 600, marginBottom: '6px' }}>
                    <FiCalendar size={11} /> Date
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>
                    {eventDate.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              )}
              {ticket.location && (
                <div style={{ padding: '14px', background: `${colors.accent}08`, borderRadius: '12px', border: `1px solid ${colors.accent}12` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: colors.accent, fontWeight: 600, marginBottom: '6px' }}>
                    <FiMapPin size={11} /> Venue
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{ticket.location}</div>
                </div>
              )}
              <div style={{ padding: '14px', background: `${colors.accent}08`, borderRadius: '12px', border: `1px solid ${colors.accent}12` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: colors.accent, fontWeight: 600, marginBottom: '6px' }}>
                  <FiUser size={11} /> Host
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{ticket.host_name}</div>
              </div>
              <div style={{ padding: '14px', background: `${colors.accent}08`, borderRadius: '12px', border: `1px solid ${colors.accent}12` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: colors.accent, fontWeight: 600, marginBottom: '6px' }}>
                  {ticket.checked_in ? <FiCheck size={11} /> : <span>🎫</span>} Status
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: ticket.checked_in ? '#10B981' : colors.accent }}>
                  {ticket.checked_in ? 'Checked In' : 'Valid'}
                </div>
              </div>
            </div>

            {/* Ticket code + QR */}
            <div style={{ textAlign: 'center', padding: '20px', background: `${colors.accent}06`, borderRadius: '14px', border: `1px dashed ${colors.accent}20`, marginBottom: '20px' }}>
              {/* Tier badge */}
              {ticket.tier_name && (
                <div style={{ marginBottom: '16px' }}>
                  <span style={{
                    fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                    color: ticket.tier_color || colors.accent,
                    background: `${ticket.tier_color || colors.accent}15`,
                    padding: '4px 12px', borderRadius: '20px'
                  }}>{ticket.tier_name}</span>
                  {ticket.price_paid > 0 && (
                    <div style={{ fontSize: '13px', fontWeight: 600, color: colors.text, marginTop: '6px', opacity: 0.7 }}>
                      R{Number(ticket.price_paid).toFixed(2)}
                    </div>
                  )}
                </div>
              )}
              {!ticket.tier_name && ticket.price_paid > 0 && (
                <div style={{ fontSize: '13px', fontWeight: 600, color: colors.text, marginBottom: '12px', opacity: 0.7 }}>
                  R{Number(ticket.price_paid).toFixed(2)}
                </div>
              )}
              {/* QR Code */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                <QRCodeSVG
                  value={`${window.location.origin}/ticket/${ticket.ticket_code}`}
                  size={140}
                  bgColor="transparent"
                  fgColor={colors.accent}
                  level="M"
                  includeMargin
                />
              </div>
              <div style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '4px', color: colors.text, fontFamily: "'Courier New', monospace" }}>
                {ticket.ticket_code}
              </div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: colors.accent, marginTop: '6px', fontWeight: 500 }}>
                Present this ticket at the event
              </div>
            </div>

            {/* Share buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={shareWhatsApp} style={{
                flex: 1, padding: '14px', borderRadius: '12px', border: 'none',
                background: '#25D366', color: 'white', fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'Inter, sans-serif', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: '8px'
              }}>
                <FiShare2 size={15} /> Share via WhatsApp
              </button>
              <button onClick={() => { navigator.clipboard?.writeText(ticketUrl); alert('Ticket link copied!'); }} style={{
                padding: '14px 18px', borderRadius: '12px', border: `1px solid ${colors.accent}30`,
                background: 'transparent', color: colors.text, fontSize: '14px', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'Inter, sans-serif'
              }}>
                Copy Link
              </button>
            </div>

            {/* Multi-ticket navigation */}
            {allTickets.length > 1 && (
              <div style={{ marginTop: '16px', padding: '12px', background: `${colors.accent}08`, borderRadius: '12px', border: `1px solid ${colors.accent}12` }}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: colors.accent, fontWeight: 600, marginBottom: '10px' }}>Your Tickets</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {allTickets.map(code => (
                    <button key={code} onClick={() => navigate(`/ticket/${code}?share=${encodeURIComponent(inviteShareLink)}&all_tickets=${encodeURIComponent(allTickets.join(','))}`)} style={{
                      padding: '8px 14px', borderRadius: '8px', border: `1px solid ${code === ticketCode ? colors.accent : colors.accent}20`,
                      background: code === ticketCode ? `${colors.accent}18` : 'transparent',
                      color: colors.text, fontSize: '12px', fontWeight: code === ticketCode ? 700 : 500,
                      cursor: 'pointer', fontFamily: 'Inter, sans-serif'
                    }}>
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* View Event Details button */}
            {inviteShareLink && (
              <button onClick={() => navigate(`/invite/${inviteShareLink}?tickets=${allTickets.join(',')}`)} style={{
                width: '100%', marginTop: '16px', padding: '14px', borderRadius: '12px',
                border: `1px solid ${colors.accent}40`, background: `${colors.accent}10`,
                color: colors.text, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '8px'
              }}>
                <FiExternalLink size={15} /> View Event Details
              </button>
            )}
          </div>
        </div>

        {/* GGR branding */}
        <div style={{ textAlign: 'center', marginTop: '24px', opacity: 0.3 }}>
          <div style={{ fontSize: '16px', color: colors.accent, marginBottom: '6px' }}>✦</div>
          <p style={{ fontSize: '10px', color: colors.text, letterSpacing: '3px', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Grand Gather Registry</p>
        </div>
      </div>
    </div>
  );
}
